import { ChatMessage } from './chat';

export type ChatMode = 'assistant' | 'code-helper' | 'analyst' | 'creative-writer';

export interface ChatModeConfig {
  id: ChatMode;
  name: string;
  description: string;
  icon: string;
  systemPrompt: string;
  temperature: number;
  maxTokens: number;
  color: string;
}

export interface Conversation {
  id: string;
  title: string;
  mode: ChatMode;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  isPinned?: boolean;
  tokenCount?: number;
}

export interface ConversationExport {
  conversation: Conversation;
  exportedAt: Date;
  version: string;
}

/**
 * STYLE GUIDE: Icons use SVG identifiers - NO emojis allowed
 * Icons map to components in src/components/icons/AppIcons.tsx
 */
export const CHAT_MODE_CONFIGS: Record<ChatMode, ChatModeConfig> = {
  'assistant': {
    id: 'assistant',
    name: 'General Assistant',
    description: 'Helpful AI assistant for general questions and tasks',
    icon: 'message-circle',
    systemPrompt: 'You are a helpful, friendly, and knowledgeable AI assistant. Provide clear, accurate, and helpful responses to user queries. Be conversational but professional.',
    temperature: 0.7,
    maxTokens: 2048,
    color: 'from-purple-500 to-blue-500'
  },
  'code-helper': {
    id: 'code-helper',
    name: 'Code Helper',
    description: 'Expert programming assistant for code review and debugging',
    icon: 'code',
    systemPrompt: 'You are an expert programming assistant with deep knowledge of software development, algorithms, and best practices. Provide clear code examples, explain complex concepts simply, and help debug issues. Format code blocks properly with syntax highlighting.',
    temperature: 0.3,
    maxTokens: 4096,
    color: 'from-green-500 to-emerald-500'
  },
  'analyst': {
    id: 'analyst',
    name: 'Data Analyst',
    description: 'Analyze data, create insights, and interpret findings',
    icon: 'bar-chart',
    systemPrompt: 'You are a professional data analyst specializing in data interpretation, statistical analysis, and visualization insights. Provide detailed analysis with actionable insights. Use clear explanations and suggest appropriate visualizations when relevant.',
    temperature: 0.4,
    maxTokens: 3072,
    color: 'from-orange-500 to-red-500'
  },
  'creative-writer': {
    id: 'creative-writer',
    name: 'Creative Writer',
    description: 'Creative writing, storytelling, and content generation',
    icon: 'pen-tool',
    systemPrompt: 'You are a creative writing assistant with expertise in storytelling, content creation, and creative expression. Help users with creative writing tasks, brainstorming ideas, and refining their work. Be imaginative and engaging.',
    temperature: 0.9,
    maxTokens: 4096,
    color: 'from-pink-500 to-purple-500'
  }
};

// Estimated tokens per character (rough approximation)
export const TOKENS_PER_CHAR = 0.25;

// Token estimation function
export function estimateTokens(text: string): number {
  return Math.ceil(text.length * TOKENS_PER_CHAR);
}

// Generate conversation title from first message
export function generateConversationTitle(firstMessage: string, maxLength: number = 50): string {
  const cleaned = firstMessage.trim().replace(/\s+/g, ' ');

  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  // Try to cut at word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
}

// Calculate total tokens in conversation
export function calculateConversationTokens(messages: ChatMessage[]): number {
  return messages.reduce((total, msg) => {
    let tokens = estimateTokens(msg.content);

    // Add tokens for files (rough estimate)
    if (msg.files && msg.files.length > 0) {
      tokens += msg.files.length * 100; // Approximate token cost per file
    }

    return total + tokens;
  }, 0);
}
